import Cesium from 'cesium';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import createTiledImageLayer from '~app/core/createTiledImageLayer';
import Location from '~app/core/location';
import Logging from '~app/core/logging';

const log = Logging.getLogger('app.components.CesiumGlobe');

// defined by manual testing
const MIN_ZOOM_HEIGHT = 5000000;
const MAX_ZOOM_HEIGHT = 50000000;

const MORPH_DURATION = 3.0;

function toCesiumSceneMode(mode) {
  return mode === '3D' ? Cesium.SceneMode.SCENE3D: Cesium.SceneMode.SCENE2D;
}

function fromCesiumSceneMode(mode) {
  return mode === Cesium.SceneMode.SCENE3D ? '3D' : '2D';
}

class CesiumGlobe extends Component {
  static propTypes = {
    allLayers: PropTypes.array,
    activeLayers: PropTypes.array,
    vectors: PropTypes.array,
    labels: PropTypes.array,
    markers: PropTypes.array,
    skyboxImages: PropTypes.array,
    onTap: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this._defaultLayer = props.defaultLayer || createTiledImageLayer('basemap-512', {
      maximumLevel: 1,
      tileSize: 512,
    });
  }

  get mode() {
    return fromCesiumSceneMode(this._scene.mode);
  }

  set mode(mode) {
    this._scene.mode = toCesiumSceneMode(mode);
  }

  getEntities(props) {
    return [...props.markers, ...props.labels];
  }

  flyTo(location, duration) {
    // If the location is not defined, don't move the camera.
    // Just re-orient.
    const destination = Cesium.defined(location) ?
      location.cartesian3(this._globe.ellipsoid) :
      this._camera.position;

    return new Promise(resolve => {
      this._camera.flyTo({
        destination,
        duration,
        complete: () => resolve(true),
        cancel: () => resolve(false),
      });
    });
  }

  cancelFlight() {
    this._camera.cancelFlight();
  }

  morph(mode) {
    return new Promise(resolve => {
      const removeListener = this._scene.morphComplete.addEventListener(() => {
        removeListener();
        resolve(this.mode);
      });

      if (mode === '3D') {
        this._scene.morphTo3D(MORPH_DURATION);
      }
      else {
        this._scene.morphTo2D(MORPH_DURATION);
      }
    });
  }

  _onTap(movement) {
    const pickedObject = this._scene.pick(movement.position);
    const cartesian = this._camera.pickEllipsoid(
      movement.position,
      this._globe.ellipsoid,
    );
    let location = null;
    if (Cesium.defined(cartesian)) {
      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      // Unpack the cartographic location into a generic (ie. non-Cesium) representation.
      location = new Location([cartographic.longitude, cartographic.latitude, cartographic.height]);
    }

    log.trace(`received tap event with object=${pickedObject} at location=${location}`);
    if (Cesium.defined(pickedObject) && Cesium.defined(pickedObject.id)) {
      this.props.onSelect(pickedObject.id, location);
    }
    else {
      this.props.onTap(location);
    }
  }

  _onCameraChanged() {
    const toDeg = Cesium.Math.toDegrees;
    const loc = this._camera.positionCartographic;
    log.trace(`camera changed ` +
      `${toDeg(loc.latitude)}, ${toDeg(loc.longitude)}, ${loc.height}`);
  }

  _updateEntities(entities) {
    const entitySet = this._dataSourceDisplay.defaultDataSource.entities;

    if (entities.length < 1) {
      entitySet.removeAll(false);
      return;
    }

    // Remove any entities in the entityCollection that we don't want anymore.
    for (let i = entitySet.values.length - 1; i >= 0 ; --i) {
      const entity = entitySet.values[i];
      if (entities.includes(entity) === false) {
        entitySet.remove(entity, false);
      }
    }

    // Add the new entities we don't have yet.
    for (const entity of entities) {
      if (entitySet.contains(entity) === false) {
        entitySet.add(entity);
      }
    }
  }

  _updateLayers(layers = [], defaultLayer = this._defaultLayer) {
    log.debug('updating globe layers');
    const {imageryLayers} = this._scene;

    if (defaultLayer !== this._defaultLayer) {
      if (imageryLayers.get(0) === this._defaultLayer) {
        imageryLayers.remove(this._defaultLayer, false);
      }
      this._defaultLayer = defaultLayer;
    }

    if (layers.length < 1) {
      imageryLayers.removeAll(false);
      imageryLayers.add(this._defaultLayer);
      return;
    }

    // Remove any data sources in the dataSourceCollection that we don't want anymore.
    for (let i = imageryLayers.length - 1; i >= 0 ; --i) {
      const layer = imageryLayers.get(i);
      if (layers.includes(layer) === false) {
        imageryLayers.remove(layer, false);
      }
    }

    // Add the new data sources we don't have yet.
    for (const layer of layers) {
      if (imageryLayers.contains(layer) === false) {
        imageryLayers.add(layer);
      }
    }
  }

  _updateActiveLayers(activeLayers = []) {
    const {imageryLayers} = this._scene;

    // Raise each active layer to the top in order.
    // This means the first one is at the bottom of the active stack,
    // and the last one is on the top.
    for (const layer of activeLayers) {
      imageryLayers.raiseToTop(layer);
    }
  }

  _updateVectors(vectors = []) {
    if (vectors.length < 1) {
      this._dataSourceCollection.removeAll(false);
      return;
    }

    // Remove any data sources in the dataSourceCollection that we don't want anymore.
    for (let i = this._dataSourceCollection.length - 1; i >= 0 ; --i) {
      const dataSource = this._dataSourceCollection.get(i);
      if (vectors.includes(dataSource) === false) {
        this._dataSourceCollection.remove(dataSource, false);
      }
    }

    // Add the new data sources we don't have yet.
    for (const dataSource of vectors) {
      if (this._dataSourceCollection.contains(dataSource) === false) {
        this._dataSourceCollection.add(dataSource);
      }
    }
  }

  _updateSkybox(skyboxImages = undefined) {
    if (Cesium.defined(this._scene.skyBox)) {
      this._scene.skyBox.destroy();
    }

    if (Cesium.defined(skyboxImages)) {
      this._scene.skyBox = new Cesium.SkyBox({
        sources: {
          positiveX: skyboxImages[0],
          negativeX: skyboxImages[1],
          positiveY: skyboxImages[2],
          negativeY: skyboxImages[3],
          positiveZ: skyboxImages[4],
          negativeZ: skyboxImages[5],
        },
      });
    }
    else {
      this._scene.skyBox = undefined;
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.markers !== this.props.markers ||
        nextProps.labels !== this.props.labels) {
      this._updateEntities(this.getEntities(nextProps));
    }
    if (nextProps.defaultLayer !== this.props.defaultLayer ||
        nextProps.allLayers !== this.props.allLayers) {
      this._updateLayers(nextProps.allLayers, nextProps.defaultLayer);
    }
    if (nextProps.activeLayers !== this.props.activeLayers) {
      this._updateActiveLayers(nextProps.activeLayers);
    }
    if (nextProps.vectors !== this.props.vectors) {
      this._updateVectors(nextProps.vectors);
    }
    if (nextProps.skyboxImages !== this.props.skyboxImages) {
      this._updateSkybox(nextProps.skyboxImages);
    }
  }

  shouldComponentUpdate() {
    // all of the props and state are handled outside of react's render
    // function and thus we only need to render the containing div one time
    // and we'll handle it from there
    return false;
  }

  componentDidMount() {
    this._widget = new Cesium.CesiumWidget(this.cesiumContainer, {
      imageryProvider: false,
    });
    this._scene = this._widget.scene;

    this._scene.sun = undefined;
    this._scene.moon = undefined;

    this._dataSourceCollection = new Cesium.DataSourceCollection();
    this._dataSourceDisplay = new Cesium.DataSourceDisplay({
      scene: this._scene,
      dataSourceCollection: this._dataSourceCollection,
    });
    this._widget.clock.onTick.addEventListener(clock => {
        this._dataSourceDisplay.update(clock.currentTime);
    });

    this._globe = this._scene.globe;
    this._camera = this._scene.camera;
    this._screenSpaceCameraController = this._scene.screenSpaceCameraController;

    this._camera.percentageChanged = 0.01;
    this._camera.changed.addEventListener(::this._onCameraChanged);

    // allow the globe to rate unconstrained
    this._camera.constrainedAxis = undefined;

    // disable look
    this._screenSpaceCameraController.enableLook = false;
    this._screenSpaceCameraController.lookEventTypes = [];
    this._screenSpaceCameraController.minimumTrackBallHeight = MIN_ZOOM_HEIGHT;

    // disable tilt
    this._screenSpaceCameraController.enableTilt = false;
    this._screenSpaceCameraController.tiltEventTypes = [];

    this._screenSpaceCameraController.minimumZoomDistance = MIN_ZOOM_HEIGHT;
    this._screenSpaceCameraController.maximumZoomDistance = MAX_ZOOM_HEIGHT;

    // HACK
    // this value is very likely dependant on the minimumZoomDistance
    // without this hack the camera zooms out very slowly when zoomed
    // all the way into the globe
    this._screenSpaceCameraController._minimumZoomRate = 5000000.0;

    this.eventHandler = new Cesium.ScreenSpaceEventHandler(this._scene.canvas);
    this.eventHandler.setInputAction(
      ::this._onTap,
      Cesium.ScreenSpaceEventType.LEFT_CLICK,
    );

    this._camera.setView({
      // start at Iowa City of course
      // the height is defined anecdotally but appears to fill most of the
      // screen vertically
      destination: Cesium.Cartesian3.fromDegrees(-91, 41, 15000000),
    });

    this._scene.mode = Cesium.SceneMode.SCENE3D;
    this._scene.completeMorphOnUserInput = false;

    this._globe.tileCacheSize = 512;

    this._updateLayers(this.props.allLayers, this.props.defaultLayer);
    this._updateVectors(this.props.vectors);
    this._updateEntities(this.getEntities(this.props));
    this._updateSkybox(this.props.skyboxImages);
  }

  componentWillUnmount() {
    if (this._widget) {
      this._widget.destroy();
    }
  }

  render() {
    return (
      <div className="CesiumGlobe"
        ref={element => this.cesiumContainer = element}
      />
    );
  }
}

export default CesiumGlobe;



// WEBPACK FOOTER //
// ./src/components/CesiumGlobe.js